import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createFirebase, loadRuntimeConfig, type FirebaseBundle } from './firebase.ts'
import { startSync, type SyncHandle } from './sync.ts'
import { useFriendStore, useStore } from './StoreProvider.tsx'

export interface AuthUser {
  uid: string
  name: string
  email: string | null
}

export interface AuthState {
  /** False until /config.json has been read. */
  ready: boolean
  /** True when a cloud project is configured. Sign-in and sharing are hidden when false. */
  cloudEnabled: boolean
  user: AuthUser | null
  vapidPublicKey: string | null
  signIn(): Promise<void>
  signOut(): Promise<void>
  deleteMyData(): Promise<void>
  /** Firebase ID token for calls to our own API, or null when signed out. */
  getIdToken(): Promise<string | null>
}

const AuthContext = createContext<AuthState | null>(null)

const LOCAL_ONLY: AuthState = {
  ready: true,
  cloudEnabled: false,
  user: null,
  vapidPublicKey: null,
  signIn: async () => {},
  signOut: async () => {},
  deleteMyData: async () => {},
  getIdToken: async () => null,
}

/**
 * Optional cloud identity.
 *
 * The app is fully usable before this resolves and stays fully usable if it
 * resolves to "no project configured" — that is a supported state, not an error.
 * Sign-in only ever adds synchronisation and sharing on top of the local store.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const store = useStore()
  const friendStore = useFriendStore()
  const [ready, setReady] = useState(false)
  const [user, setUser] = useState<AuthUser | null>(null)
  const [vapidPublicKey, setVapidPublicKey] = useState<string | null>(null)
  const bundleRef = useRef<FirebaseBundle | null>(null)
  const syncRef = useRef<SyncHandle | null>(null)
  const unsubscribeAuthRef = useRef<(() => void) | null>(null)
  const lastUidRef = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function boot() {
      const config = await loadRuntimeConfig()
      if (cancelled) return

      setVapidPublicKey(config.vapidPublicKey ?? null)

      if (!config.firebase) {
        setReady(true)
        return
      }

      try {
        const bundle = await createFirebase(config.firebase)
        if (cancelled) return
        bundleRef.current = bundle

        const { onAuthStateChanged } = await import('firebase/auth')
        unsubscribeAuthRef.current = onAuthStateChanged(bundle.auth, (fbUser) => {
          if (cancelled) return
          syncRef.current?.stop()
          syncRef.current = null

          if (!fbUser) {
            setUser(null)
            return
          }

          // A different account than the one whose notes are in local storage —
          // clear first. Otherwise setOwner would re-stamp the previous user's
          // annotations with this uid and upload them into the new account. The
          // friend list goes with them: it holds other people's email addresses,
          // and the second account on this phone has no business inheriting them.
          if (lastUidRef.current && lastUidRef.current !== fbUser.uid) {
            store.clear()
            friendStore.clear()
          }
          lastUidRef.current = fbUser.uid

          const next: AuthUser = {
            uid: fbUser.uid,
            name: fbUser.displayName ?? fbUser.email ?? 'En venn',
            email: fbUser.email,
          }
          setUser(next)
          syncRef.current = startSync(
            store,
            friendStore,
            bundle.remote,
            next.uid,
            next.name,
            (error) => console.warn('Synkronisering feilet', error),
          )

          // Two catch-up calls, both best-effort and both independent: whatever they
          // convert arrives on the next snapshot of a listener that is already
          // running, so a failure here costs a refresh at worst and must never block
          // signing in. They are fired separately so one failing does not skip the
          // other.
          void bundle
            .getIdToken()
            .then((token) => {
              if (!token) return
              const headers = { Authorization: `Bearer ${token}` }
              // Shares addressed to this email before it had an account.
              void fetch('/api/claim-shares', { method: 'POST', headers }).catch(() => {})
              // Friendships proposed to this address before it had an account: held
              // by the server until now, and turned into real requests at this first
              // sign-in (R9).
              void fetch('/api/friends/reconcile', { method: 'POST', headers }).catch(() => {})
            })
            .catch(() => {})
        })
      } catch (error) {
        console.warn('Kunne ikke starte Firebase — fortsetter lokalt', error)
      } finally {
        if (!cancelled) setReady(true)
      }
    }

    void boot()
    return () => {
      cancelled = true
      syncRef.current?.stop()
      // Without this the auth listener outlives the effect and can start a fresh
      // sync that nothing holds a reference to and stop() will never reach.
      unsubscribeAuthRef.current?.()
      unsubscribeAuthRef.current = null
    }
  }, [store, friendStore])

  const signIn = useCallback(async () => {
    await bundleRef.current?.signIn()
  }, [])

  const signOut = useCallback(async () => {
    syncRef.current?.stop()
    syncRef.current = null
    lastUidRef.current = null
    await bundleRef.current?.signOut()
    // Clear the local copies too: a second Google account on the same device must not
    // inherit the previous user's notes, nor their friends' email addresses.
    store.clear()
    friendStore.clear()
  }, [store, friendStore])

  const deleteMyData = useCallback(async () => {
    const bundle = bundleRef.current
    const uid = user?.uid
    if (bundle && uid) {
      // Both halves are best-effort: the local wipe must happen even when the
      // network does not, because a destructive control that silently does nothing
      // is the worst outcome here.
      await bundle.remote
        .deleteAllOwnedBy(uid)
        .catch((error) => console.warn('Kunne ikke slette fra skyen', error))
      await fetch('/api/delete-my-data', {
        method: 'POST',
        headers: { Authorization: `Bearer ${(await bundle.getIdToken()) ?? ''}` },
      }).catch(() => {})
    }
    store.clear()
    friendStore.clear()
    lastUidRef.current = null
    await bundle?.signOut()
  }, [store, friendStore, user])

  const getIdToken = useCallback(async () => {
    return bundleRef.current?.getIdToken() ?? null
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      ready,
      cloudEnabled: bundleRef.current !== null,
      user,
      vapidPublicKey,
      signIn,
      signOut,
      deleteMyData,
      getIdToken,
    }),
    [ready, user, vapidPublicKey, signIn, signOut, deleteMyData, getIdToken],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  return useContext(AuthContext) ?? LOCAL_ONLY
}
